import { useEffect, useState } from 'react';
import InfoFaltanteModal from './InfoFaltanteModal';
import StatusBadge from '@/components/base/StatusBadge';
import PagarModal from '@/pages/home/components/PagarModal';
import { supabase } from '@/lib/supabase';

type PromoKey = 'streaming' | 'membresia' | 'descuentos';

interface AppNotification {
  id: string;
  title: string;
  body: string;
  category: string;
  read_at: string | null;
  created_at: string;
}

interface ScreenInicioProps {
  user: {
    id: string; folio: string; nombre: string; apellido: string;
    nombreCompleto: string; planNombre: string; plan: string;
    planPrecio: number; estado: string; fechaContratacion: string;
    fechaCarenciaFin: string; primer_pago_realizado: boolean;
    curp?: string; ciudad?: string; estado_residencia?: string;
  };
  onAddBeneficiary?: () => void;
  onGoToPagos?: () => void;
  onGoToPlan?: () => void;
}

export default function ScreenInicio({ user, onAddBeneficiary, onGoToPagos, onGoToPlan }: ScreenInicioProps) {
  const calcDias = () => {
    try {
      if (!user.fechaCarenciaFin) return 0;
      const fin = new Date(user.fechaCarenciaFin);
      const hoy = new Date();
      return Math.max(0, Math.ceil((fin.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24)));
    } catch {
      return 0;
    }
  };

  const diasCarencia = calcDias();
  const [showPagarModal, setShowPagarModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [ciudadGuardada, setCiudadGuardada] = useState(user.ciudad || user.estado_residencia || '');
  const [affiliateCopied, setAffiliateCopied] = useState(false);
  const [activePromo, setActivePromo] = useState<PromoKey | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const infoFaltante = !ciudadGuardada;
  const firstName = (user.nombre || user.nombreCompleto || 'Cliente').split(' ')[0];
  const currentPaymentMonth = new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  const unreadCount = notifications.filter((item) => !item.read_at).length;

  const loadNotifications = async () => {
    if (!user.id && !user.folio) return;
    setNotificationsLoading(true);

    const filters = [
      `audience.eq.all`,
      `member_id.eq.${user.id}`,
      `folio.eq.${user.folio}`,
    ];

    const { data, error } = await supabase
      .from('app_notifications')
      .select('*')
      .or(filters.join(','))
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) setNotifications(data as AppNotification[]);
    if (error) console.warn('No se pudieron cargar notificaciones:', error.message);
    setNotificationsLoading(false);
  };

  useEffect(() => {
    loadNotifications();
  }, [user.id, user.folio]);

  const markNotificationRead = async (notificationId: string) => {
    const readAt = new Date().toISOString();
    setNotifications((items) => items.map((item) => (
      item.id === notificationId ? { ...item, read_at: item.read_at || readAt } : item
    )));

    const { error } = await supabase
      .from('app_notifications')
      .update({ read_at: readAt })
      .eq('id', notificationId)
      .is('read_at', null);

    if (error) console.warn('No se pudo marcar notificacion leida:', error.message);
  };

  const handleAffiliateLink = async () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/?ref=${encodeURIComponent(user.folio)}`;
    await navigator.clipboard.writeText(link).catch(() => {});
    setAffiliateCopied(true);
    setTimeout(() => setAffiliateCopied(false), 2500);
  };

  const savePreparedPayment = async (payload: {
    metodo: 'tarjeta' | 'tienda';
    referencia?: string;
    domiciliado?: boolean;
    cardLast4?: string;
    amount: number;
    period: string;
  }) => {
    if (!user.id || user.id === 'admin') return;

    const paymentIntent = {
      member_id: user.id,
      folio: user.folio,
      monto: payload.amount,
      moneda: 'MXN',
      periodo: payload.period,
      metodo: payload.metodo,
      status: payload.metodo === 'tienda' ? 'referencia_generada' : 'tarjeta_capturada_pendiente_procesador',
      procesador: 'pendiente_integracion',
      referencia_tienda: payload.referencia || null,
      card_last4: payload.cardLast4 || null,
      domiciliado: Boolean(payload.domiciliado),
      payload: {
        plan: user.plan,
        planNombre: user.planNombre,
        source: 'inicio_pagar_modal',
      },
    };

    const { error: intentError } = await supabase.from('payment_intents').insert(paymentIntent);
    if (intentError) console.warn('Payment intent pendiente de esquema:', intentError.message);

    if (payload.metodo === 'tarjeta' && payload.cardLast4) {
      const { error: methodError } = await supabase.from('payment_methods').insert({
        member_id: user.id,
        folio: user.folio,
        tipo: 'tarjeta',
        procesador: 'pendiente_integracion',
        card_last4: payload.cardLast4,
        domiciliado: Boolean(payload.domiciliado),
        status: 'pendiente_procesador',
      });
      if (methodError) console.warn('Payment method pendiente de esquema:', methodError.message);
    }

    const { data: pendingPayments } = await supabase
      .from('pagos')
      .select('id')
      .eq('member_id', user.id)
      .eq('estado', 'pendiente')
      .limit(1);

    const pagoPatch = {
      metodo: payload.metodo,
      metodo_pago: payload.metodo,
      referencia_tienda: payload.referencia || null,
      referencia_oxxo: payload.referencia || null,
      procesador: 'pendiente_integracion',
      processor_status: paymentIntent.status,
      domiciliado: Boolean(payload.domiciliado),
      card_last4: payload.cardLast4 || null,
      folio: user.folio,
      periodo: payload.period,
    };

    if (pendingPayments && pendingPayments.length > 0) {
      const { error } = await supabase.from('pagos').update(pagoPatch).eq('id', pendingPayments[0].id);
      if (error) console.warn('Pago pendiente de esquema:', error.message);
      return;
    }

    const { error } = await supabase.from('pagos').insert({
      member_id: user.id,
      monto: payload.amount,
      mes: payload.period,
      estado: 'pendiente',
      ...pagoPatch,
    });
    if (error) console.warn('Pago insert pendiente de esquema:', error.message);
  };

  if (activePromo) {
    return (
      <PromoDetailScreen
        promo={activePromo}
        folio={user.folio}
        onBack={() => setActivePromo(null)}
        onCopyAffiliate={handleAffiliateLink}
        affiliateCopied={affiliateCopied}
      />
    );
  }

  if (showNotifications) {
    return (
      <NotificationsScreen
        notifications={notifications}
        loading={notificationsLoading}
        onBack={() => setShowNotifications(false)}
        onRead={markNotificationRead}
        onRefresh={loadNotifications}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white pb-28 text-[#34313F]">
      <header className="bg-[#132D86] px-5 pb-4 pt-7 text-white">
        <div className="flex items-center justify-between">
          <h1 className="text-[1.38rem] font-black leading-tight tracking-[-0.01em]">Hola, {firstName}</h1>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setShowNotifications(true)}
              className="relative flex h-8 w-8 items-center justify-center rounded-full text-white"
              aria-label="Notificaciones"
            >
              <i className="ri-notification-3-line text-[1.25rem]" />
              {unreadCount > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[#E11D48] px-1 text-[8px] font-black">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button className="flex h-8 w-8 items-center justify-center rounded-full text-white" aria-label="Menu">
              <i className="ri-menu-line text-[1.55rem]" />
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="px-5 py-5">
          <button
            onClick={() => setActivePromo('streaming')}
            className="block w-full min-h-[150px] overflow-hidden rounded-[24px] border border-[#E3E1EA] bg-[#F7F6FF] text-left shadow-sm transition-transform active:scale-[0.99]"
            aria-label="Ver beneficio de meses gratis de streaming"
          >
            <img
              src="/coverly-streaming-home-banner.png"
              alt="Meses gratis de streaming afiliando nuevos socios Coverly"
              className="h-full min-h-[150px] w-full object-cover"
            />
          </button>
        </section>

        <section className="grid grid-cols-4 gap-3 px-5">
          <QuickAction icon="ri-user-add-line" label="Agregar" onClick={onAddBeneficiary} />
          <QuickAction icon={affiliateCopied ? 'ri-check-line' : 'ri-user-shared-line'} label={affiliateCopied ? 'Copiado' : 'Afiliar'} onClick={handleAffiliateLink} />
          <QuickAction icon="ri-bank-card-line" label="Pagar" onClick={() => setShowPagarModal(true)} />
          <QuickAction icon="ri-more-2-fill" label="Mas acciones" onClick={onGoToPlan} />
        </section>

        {affiliateCopied && (
          <section className="mx-5 mt-4 rounded-2xl border border-[#C9F0E2] bg-[#F0FBF7] px-4 py-3">
            <p className="text-xs font-bold leading-5 text-[#177A5E]">
              Link de afiliacion copiado. Puedes enviarlo por WhatsApp, mensaje o redes sociales.
            </p>
          </section>
        )}

        <section className="mt-6 px-5">
          <StatusBadge estado={user.estado as 'activa' | 'carencia' | 'suspendida'} diasRestantes={diasCarencia} />
        </section>

        <section className="mt-6 grid gap-4 px-5">
          <PromoBanner
            image="/coverly-membership-free-banner.png"
            alt="2 meses gratis de membresia afiliando 5 nuevos socios Coverly"
            onClick={() => setActivePromo('membresia')}
          />
          <PromoBanner
            image="/coverly-discounts-banner.png"
            alt="Descuentos para miembros en negocios afiliados Coverly"
            onClick={() => setActivePromo('descuentos')}
          />
        </section>

        {infoFaltante && !showInfoModal && (
          <section className="mx-5 mt-4 rounded-[24px] border border-[#F4D28B] bg-[#FFF8E8] p-4">
            <div className="flex gap-3">
              <i className="ri-error-warning-line mt-0.5 text-lg text-[#F59E0B]" />
              <div className="flex-1">
                <p className="text-sm font-black text-[#6E4500]">Completa tu informacion</p>
                <p className="mt-1 text-xs leading-5 text-[#8A5A00]">Falta confirmar ciudad y estado para mantener tu perfil completo.</p>
                <button onClick={() => setShowInfoModal(true)} className="mt-3 rounded-full bg-[#F59E0B] px-4 py-2 text-xs font-black text-white">
                  Completar ahora
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="mx-5 mt-6 rounded-[24px] bg-[#F6F4FF] p-5">
          <p className="text-xs font-black uppercase tracking-wide text-[#7C7589]">Datos de cuenta</p>
          <div className="mt-4 grid gap-3">
            <InfoRow label="Titular" value={user.nombreCompleto || user.nombre} />
            <InfoRow label="Plan" value={`Plan ${user.planNombre} - $${user.planPrecio}/mes`} />
            <InfoRow label="Registro" value={user.fechaContratacion || '-'} />
            <InfoRow label="Ciudad" value={ciudadGuardada || user.ciudad || user.estado_residencia || '-'} />
          </div>
        </section>
      </main>

      {showInfoModal && (
        <InfoFaltanteModal
          memberId={user.id || ''}
          onClose={() => setShowInfoModal(false)}
          onGuardado={(ciudad: string, estado: string) => {
            setCiudadGuardada(ciudad || estado);
            setShowInfoModal(false);
          }}
        />
      )}

      {showPagarModal && (
        <PagarModal
          onClose={() => setShowPagarModal(false)}
          monto={user.planPrecio}
          mes={currentPaymentMonth}
          folio={user.folio}
          planNombre={user.planNombre}
          onPaymentPrepared={savePreparedPayment}
        />
      )}
    </div>
  );
}

function QuickAction({ icon, label, onClick, href }: { icon: string; label: string; onClick?: () => void; href?: string }) {
  const content = (
    <>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#132D86] text-white shadow-sm shadow-[#132D86]/10">
        <i className={`${icon} text-[1.4rem]`} />
      </span>
      <span className="mt-2 text-center text-xs font-semibold leading-tight text-[#66636D]">{label}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="nofollow noreferrer" className="flex flex-col items-center">
        {content}
      </a>
    );
  }

  return (
    <button onClick={onClick} className="flex flex-col items-center">
      {content}
    </button>
  );
}

function PromoBanner({
  title,
  body,
  cta,
  image,
  alt,
  onClick,
}: {
  title?: string;
  body?: string;
  cta?: string;
  image?: string;
  alt?: string;
  onClick?: () => void;
}) {
  const content = image ? (
    <img src={image} alt={alt || ''} className="min-h-[132px] w-full object-cover" />
  ) : (
    <div className="flex min-h-[132px] items-center px-5 py-4">
      <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl bg-[#F1EFFF] text-[#5A4FC7]">
        <i className="ri-image-line text-3xl" />
      </div>
      <div className="min-w-0 flex-1 px-4">
        <p className="text-base font-black leading-tight text-[#4A4852]">{title}</p>
        <p className="mt-1 text-sm leading-5 text-[#66636D]">{body}</p>
        <p className="mt-3 text-sm font-black text-[#23769B]">{cta}</p>
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="overflow-hidden rounded-[24px] border border-[#E3E1EA] bg-white text-left shadow-sm transition-transform active:scale-[0.99]"
      >
        {content}
      </button>
    );
  }

  return (
    <article className="overflow-hidden rounded-[24px] border border-[#E3E1EA] bg-white shadow-sm">
      {content}
    </article>
  );
}

function NotificationsScreen({
  notifications,
  loading,
  onBack,
  onRead,
  onRefresh,
}: {
  notifications: AppNotification[];
  loading: boolean;
  onBack: () => void;
  onRead: (notificationId: string) => void;
  onRefresh: () => void;
}) {
  const unreadCount = notifications.filter((item) => !item.read_at).length;

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleDateString('es-MX', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6FF] pb-8 text-[#34313F]">
      <header className="sticky top-0 z-20 bg-[#F7F6FF]/95 px-5 pb-3 pt-5 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#534AB7] shadow-sm"
            aria-label="Volver"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-black uppercase tracking-wide text-[#716AA8]">Mensajes internos</p>
            <h1 className="text-lg font-black text-[#211B4F]">Notificaciones</h1>
          </div>
          <button
            onClick={onRefresh}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#534AB7] shadow-sm"
            aria-label="Actualizar"
          >
            <i className="ri-refresh-line text-lg" />
          </button>
        </div>
      </header>

      <main className="px-5">
        <section className="rounded-[26px] bg-[#132D86] p-5 text-white shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15">
              <i className="ri-notification-3-line text-xl" />
            </span>
            <div>
              <p className="text-sm font-black">Centro de mensajes Coverly</p>
              <p className="mt-1 text-xs font-semibold text-white/70">
                {unreadCount > 0 ? `${unreadCount} mensaje${unreadCount === 1 ? '' : 's'} sin leer` : 'No tienes mensajes pendientes'}
              </p>
            </div>
          </div>
        </section>

        <section className="mt-5 grid gap-3">
          {loading ? (
            <div className="rounded-[24px] bg-white p-8 text-center shadow-sm">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#534AB7] border-t-transparent" />
              <p className="mt-3 text-sm font-semibold text-[#66636D]">Cargando mensajes...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="rounded-[24px] bg-white p-8 text-center shadow-sm">
              <i className="ri-notification-off-line text-4xl text-[#C8C4D8]" />
              <p className="mt-3 text-sm font-black text-[#4A4852]">Sin notificaciones</p>
              <p className="mt-1 text-xs leading-5 text-[#7C7589]">Aqui veras avisos, beneficios y mensajes importantes de Coverly.</p>
            </div>
          ) : (
            notifications.map((item) => (
              <button
                key={item.id}
                onClick={() => onRead(item.id)}
                className={`w-full rounded-[24px] border p-4 text-left shadow-sm transition-transform active:scale-[0.99] ${
                  item.read_at
                    ? 'border-[#ECE9F5] bg-white'
                    : 'border-[#D8D3F4] bg-[#F1EFFF]'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                    item.read_at ? 'bg-[#F4F2FA] text-[#8D87A7]' : 'bg-[#534AB7] text-white'
                  }`}>
                    <i className={item.category === 'beneficio' ? 'ri-gift-line' : item.category === 'pago' ? 'ri-bank-card-line' : 'ri-message-3-line'} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-black leading-5 text-[#211B4F]">{item.title}</p>
                      {!item.read_at && <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-[#E11D48]" />}
                    </div>
                    <p className="mt-1 text-sm leading-5 text-[#5F5A6E]">{item.body}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="rounded-full bg-white/70 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-[#716AA8]">
                        {item.category || 'Aviso'}
                      </span>
                      <span className="text-[11px] font-semibold text-[#8D87A7]">{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </section>
      </main>
    </div>
  );
}

const promoDetails: Record<PromoKey, {
  title: string;
  eyebrow: string;
  image: string;
  summary: string;
  bullets: string[];
  note: string;
  actionLabel?: string;
}> = {
  streaming: {
    eyebrow: 'Coverly Contigo',
    title: 'Meses gratis de streaming',
    image: '/coverly-streaming-home-banner.png',
    summary: 'Como miembro activo de Coverly puedes recibir meses gratis de entretenimiento digital en plataformas participantes.',
    bullets: [
      'Al cumplir 4 meses activo y sin retrasos, puedes recibir 1 mes gratis de streaming.',
      'Tambien puedes obtener este beneficio cuando una persona afiliada con tu ID activa su membresia.',
      'Las plataformas disponibles pueden incluir opciones como Netflix, Disney+, Prime Video o Spotify, segun disponibilidad.',
      'El beneficio se valida por periodo mensual. Afiliar varias personas en el mismo mes no significa acumular automaticamente un mes por cada una.',
    ],
    note: 'Coverly revisa que la membresia este activa y sin pagos vencidos antes de asignar el beneficio.',
    actionLabel: 'Copiar mi link de afiliacion',
  },
  membresia: {
    eyebrow: 'Referidos activos',
    title: 'Hasta 2 meses gratis de membresia',
    image: '/coverly-membership-free-banner.png',
    summary: 'Si afilias 5 nuevos socios con tu ID Coverly, puedes recibir hasta 2 meses de membresia gratis sin perder tu estatus activo.',
    bullets: [
      'Los 5 nuevos socios deben registrarse usando tu link o tu folio de afiliacion.',
      'Cada socio referido debe activar su membresia para que cuente dentro del beneficio.',
      'La bonificacion se aplica como meses de membresia cubiertos por Coverly.',
      'Durante esos meses sigues activo y conservas tu acceso a beneficios del programa.',
    ],
    note: 'La aplicacion deja listo tu folio para compartir. La validacion final se realiza con los registros y pagos confirmados.',
    actionLabel: 'Copiar mi link de afiliacion',
  },
  descuentos: {
    eyebrow: 'Negocios afiliados',
    title: 'Descuentos para miembros',
    image: '/coverly-discounts-banner.png',
    summary: 'Los clientes activos pueden acceder a descuentos y beneficios en negocios con convenio Coverly.',
    bullets: [
      'Beneficios disponibles en categorias como supermercados, cines, clinicas, hospitales y comercios afiliados.',
      'Para usar un convenio, tu membresia debe estar activa y sin pagos vencidos.',
      'Algunos descuentos pueden pedir tu folio Coverly o una validacion desde la app.',
      'La lista de negocios, porcentajes y condiciones puede variar segun ciudad y convenio vigente.',
    ],
    note: 'Esta seccion se ira actualizando conforme se agreguen convenios disponibles para miembros Coverly.',
  },
};

function PromoDetailScreen({
  promo,
  folio,
  onBack,
  onCopyAffiliate,
  affiliateCopied,
}: {
  promo: PromoKey;
  folio: string;
  onBack: () => void;
  onCopyAffiliate: () => void;
  affiliateCopied: boolean;
}) {
  const detail = promoDetails[promo];

  return (
    <div className="min-h-screen bg-[#F7F6FF] pb-8 text-[#34313F]">
      <header className="sticky top-0 z-20 bg-[#F7F6FF]/95 px-5 pb-3 pt-5 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#534AB7] shadow-sm"
            aria-label="Volver"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wide text-[#716AA8]">{detail.eyebrow}</p>
            <h1 className="truncate text-lg font-black text-[#211B4F]">{detail.title}</h1>
          </div>
        </div>
      </header>

      <main className="px-5">
        <section className="overflow-hidden rounded-[26px] border border-[#E3E1EA] bg-white shadow-sm">
          <img src={detail.image} alt={detail.title} className="aspect-[18/7] w-full object-cover" />
        </section>

        <section className="mt-5 rounded-[26px] bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-wide text-[#534AB7]">Como funciona</p>
          <p className="mt-2 text-xl font-black leading-tight text-[#211B4F]">{detail.title}</p>
          <p className="mt-3 text-sm leading-6 text-[#5F5A6E]">{detail.summary}</p>

          <div className="mt-5 grid gap-3">
            {detail.bullets.map((item) => (
              <div key={item} className="flex gap-3 rounded-2xl bg-[#F7F6FF] p-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#E9E6FF] text-[#534AB7]">
                  <i className="ri-check-line text-sm" />
                </span>
                <p className="text-sm font-semibold leading-5 text-[#4A4852]">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-4 rounded-[24px] border border-[#D8D3F4] bg-[#F1EFFF] p-4">
          <p className="text-xs font-black uppercase tracking-wide text-[#716AA8]">Tu ID Coverly</p>
          <div className="mt-2 flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
            <span className="font-mono text-sm font-black text-[#211B4F]">{folio || 'CVR-PENDIENTE'}</span>
            {detail.actionLabel && (
              <button
                onClick={onCopyAffiliate}
                className="rounded-full bg-[#534AB7] px-4 py-2 text-xs font-black text-white"
              >
                {affiliateCopied ? 'Copiado' : 'Copiar link'}
              </button>
            )}
          </div>
          <p className="mt-3 text-xs font-semibold leading-5 text-[#625C78]">{detail.note}</p>
        </section>
      </main>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#E4DFF6] pb-3 last:border-0 last:pb-0">
      <span className="text-xs font-semibold text-[#7C7589]">{label}</span>
      <span className="text-right text-xs font-black text-[#34313F]">{value}</span>
    </div>
  );
}
